import { createContext, useCallback, useContext, useMemo, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import { type ApiError, api, type Session } from '@whinerp/client';

/**
 * The signed-in session.
 *
 * One query, read once before the shell renders. Navigation, search scope and
 * the agent tool registry all derive from the entitlement set it returns, which
 * is what makes switching a module off take effect everywhere at once rather
 * than in whichever place remembered to check.
 */

export type EntitlementState = Session['entitlements'][number];

export interface SessionContextValue {
  session: Session;
  /** True only when the entitlement is on. Locked and off are both false. */
  isEnabled: (key: string) => boolean;
  /** The full answer, for anything that has to explain itself. */
  entitlement: (key: string) => EntitlementState | null;
  can: (capability: string) => boolean;
  setPreference: (patch: Partial<Session['preferences']>) => void;
  refresh: () => void;
}

const SessionContext = createContext<SessionContextValue | null>(null);

export const SESSION_QUERY_KEY = ['session'] as const;

export function useSessionQuery(): UseQueryResult<Session, ApiError> {
  return useQuery<Session, ApiError>({
    queryKey: SESSION_QUERY_KEY,
    queryFn: () => api.getSession(),
    // A 401 here is the ordinary signed-out case, not a failure to report.
    retry: false,
  });
}

export function SessionProvider({
  session,
  children,
}: {
  session: Session;
  children: ReactNode;
}): JSX.Element {
  const queryClient = useQueryClient();

  const preferences = useMutation({
    mutationFn: (patch: Partial<Session['preferences']>) => api.updateMyPreferences({ body: patch }),
    onSuccess: (next) => {
      queryClient.setQueryData(SESSION_QUERY_KEY, next);
    },
  });

  const byKey = useMemo(() => {
    const map = new Map<string, EntitlementState>();
    for (const entry of session.entitlements) map.set(entry.key, entry);
    return map;
  }, [session.entitlements]);

  const capabilities = useMemo(() => new Set(session.capabilities), [session.capabilities]);

  const setPreference = useCallback(
    (patch: Partial<Session['preferences']>) => {
      preferences.mutate(patch);
    },
    [preferences],
  );

  const refresh = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: SESSION_QUERY_KEY });
  }, [queryClient]);

  const value = useMemo<SessionContextValue>(
    () => ({
      session,
      isEnabled: (key) => byKey.get(key)?.state === 'on',
      entitlement: (key) => byKey.get(key) ?? null,
      // A workspace administrator holds every capability; the server says so
      // too, and this mirrors that rather than deciding it independently.
      can: (capability) => session.user.isWorkspaceAdmin || capabilities.has(capability),
      setPreference,
      refresh,
    }),
    [session, byKey, capabilities, setPreference, refresh],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionContextValue {
  const context = useContext(SessionContext);
  if (!context) {
    throw new Error('useSession was called outside a SessionProvider. Only authenticated screens have one.');
  }
  return context;
}

/**
 * Whether a feature is available, and why not when it is not.
 *
 * Returns the reason rather than a boolean so a screen can render the right
 * CapabilityUnavailable: "not on your plan" and "switched off" need different
 * words and different next steps.
 */
export function useEntitlement(key: string): {
  enabled: boolean;
  state: 'on' | 'off' | 'plan-locked' | 'unknown';
  entry: EntitlementState | null;
} {
  const { entitlement } = useSession();
  const entry = entitlement(key);
  return {
    enabled: entry?.state === 'on',
    state: entry?.state ?? 'unknown',
    entry,
  };
}
